import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import JSZip from 'jszip';
import Button from '../../components/ui/Button';
import ApplicationFilters from './components/ApplicationFilters';
import ApplicationTable from './components/ApplicationTable';
import BulkActionsBar from './components/BulkActionsBar';
import ProcessingStats from './components/ProcessingStats';
import DocumentViewerModal from './components/DocumentViewerModal';
import ApplicationDetailsModal from './components/ApplicationDetailsModal';
import EmailComposerModal from './components/EmailComposerModal';
import BulkEmailComposerModal from './components/BulkEmailComposerModal';
import StatusSelectionModal from './components/StatusSelectionModal';
import { sendStatusUpdate, sendApplicationSubmittedEmail, sendApplicationApprovedEmail, sendApplicationRejectedEmail, sendAdminRedeemedNotification } from '../../services/emailService';
import DatabaseQueryLogger from './components/DatabaseQueryLogger';
import { logBulkAction, logDocumentView, logEmailSent } from '../../services/auditLogService';
import { logSupabaseError } from '../../services/errorLogService';

const ApplicationProcessing = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    dealership: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [documentModal, setDocumentModal] = useState({ isOpen: false, application: null });
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, application: null });
  const [emailModal, setEmailModal] = useState({ isOpen: false });
  const [applications, setApplications] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [isBulkEmailModalOpen, setIsBulkEmailModalOpen] = useState(false);
  const [statusSelectionModal, setStatusSelectionModal] = useState({ isOpen: false, action: null });
  const [loading, setLoading] = useState(true);
  const [showVINTestingPanel, setShowVINTestingPanel] = useState(false);
  const [selectedApplicationForLogger, setSelectedApplicationForLogger] = useState(null);
  const [isLoggerOpen, setIsLoggerOpen] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);

  // Protect route - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchApplicationsData();
    }
  }, [user]);

  const fetchApplicationsData = async () => {
    try {
      setLoading(true);

      // Fetch dealerships
      const { data: dealershipsData, error: dealershipsError } = await supabase?.from('dealerships')?.select('id, name, city, email, phone')?.eq('status', 'active')?.order('name');

      if (dealershipsError) {
        // Log error to centralized system
        await logSupabaseError({
          error: dealershipsError,
          query: 'fetchApplicationsData - dealerships',
          table: 'dealerships',
          operation: 'SELECT',
          userEmail: user?.email,
          componentName: 'ApplicationProcessing',
          functionName: 'fetchApplicationsData'
        });
        throw new Error(dealershipsError?.message || dealershipsError?.toString() || 'Failed to fetch dealerships');
      }

      // Fetch applications with dealership info
      const { data: appsData, error: appsError } = await supabase?.from('applications')?.select(`
          *,
          dealerships (
            id,
            name,
            city,
            email
          ),
          vouchers (
            voucher_code
          )
        `)?.order('application_date', { ascending: false });

      if (appsError) {
        // Log error to centralized system
        await logSupabaseError({
          error: appsError,
          query: 'fetchApplicationsData - applications',
          table: 'applications',
          operation: 'SELECT',
          userEmail: user?.email,
          componentName: 'ApplicationProcessing',
          functionName: 'fetchApplicationsData'
        });
        throw new Error(appsError?.message || appsError?.toString() || 'Failed to fetch applications');
      }

      // Format dealerships
      const formattedDealerships = dealershipsData?.map(d => ({
        id: d?.id,
        name: d?.name,
        logo: null // Logo field not in DB, will be handled separately if needed
      })) || [];

      // Format applications
      const formattedApplications = appsData?.map(app => {
        // For companies, show "Company Name (Contact Person)"
        let displayName = app?.customer_name;
        if (app?.customer_gender === 'company' && app?.company_name) {
          displayName = `${app?.company_name} (${app?.customer_name})`;
        }
        
        return {
          id: app?.id,
          customerName: displayName,
          companyName: app?.company_name,
          customerGender: app?.customer_gender,
          email: app?.customer_email,
          phone: app?.customer_phone,
          street: null, // Not in DB schema
          postalCode: null, // Not in DB schema
          city: null, // Not in DB schema
          birthdate: null, // Not in DB schema
          vehicleModel: app?.vehicle_model,
          chassisNumber: app?.chassis_number,
          customerLicensePlate: app?.customer_license_plate,
          registrationDate: null, // Not in DB schema
          dealershipName: app?.dealerships?.name || 'Unknown',
          dealershipLogo: null,
          dealershipId: app?.dealership_id,
          applicationDate: new Date(app?.application_date)?.toLocaleDateString('de-DE'),
          status: app?.application_status,
          documentUrl: app?.vehicle_document_url, // Vehicle registration document URL from Supabase Storage
          serviceVoucher: false, // Would need to be added to schema if required
          customerNumber: null, // Would come from vouchers table
          voucherCode: app?.vouchers?.[0]?.voucher_code || null, // Get voucher code from joined vouchers table
          wantsAnnualRenewal: app?.wants_annual_renewal,
          notes: app?.notes,
          premiumAmount: app?.premium_amount
        };
      }) || [];

      setDealerships(formattedDealerships);
      setApplications(formattedApplications);

    } catch (error) {
      console.error('Error fetching applications data:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = applications?.filter((app) => {
    if (filters?.status !== 'all' && app?.status !== filters?.status) return false;
    if (filters?.dealership !== 'all' && app?.dealershipId !== filters?.dealership) return false;
    
    // Fix date filtering - handle German date format (DD.MM.YYYY)
    if (filters?.dateFrom) {
      const appDateParts = app?.applicationDate?.split('.');
      if (appDateParts?.length === 3) {
        const appDate = new Date(`${appDateParts[2]}-${appDateParts[1]}-${appDateParts[0]}`);
        const filterDate = new Date(filters?.dateFrom);
        if (appDate < filterDate) return false;
      }
    }
    
    if (filters?.dateTo) {
      const appDateParts = app?.applicationDate?.split('.');
      if (appDateParts?.length === 3) {
        const appDate = new Date(`${appDateParts[2]}-${appDateParts[1]}-${appDateParts[0]}`);
        const filterDate = new Date(filters?.dateTo);
        if (appDate > filterDate) return false;
      }
    }
    
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      return app?.customerName?.toLowerCase()?.includes(searchLower) ||
      app?.email?.toLowerCase()?.includes(searchLower) || 
      app?.chassisNumber?.toLowerCase()?.includes(searchLower) ||
      app?.customerLicensePlate?.toLowerCase()?.includes(searchLower);
    }
    return true;
  });

  const stats = {
    total: applications?.length,
    unconfirmed: applications?.filter((a) => a?.status === 'unbestätigt')?.length,
    confirmed: applications?.filter((a) => a?.status === 'bestätigt')?.length,
    applied: applications?.filter((a) => a?.status === 'processing')?.length,
    approved: applications?.filter((a) => a?.status === 'approved')?.length,
    rejected: applications?.filter((a) => a?.status === 'rejected')?.length,
    redeemed: applications?.filter((a) => a?.status === 'redeemed')?.length
  };

  const handleSelectAll = () => {
    if (selectedIds?.length === filteredApplications?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredApplications?.map((app) => app?.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
    prev?.includes(id) ? prev?.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleApprove = async (id) => {
    try {
      const { error } = await supabase?.from('applications')?.update({ 
          application_status: 'approved',
          approval_date: new Date()?.toISOString()
        })?.eq('id', id);

      if (error) {
        // Log error to centralized system
        await logSupabaseError({
          error,
          query: 'handleApprove',
          table: 'applications',
          operation: 'UPDATE',
          userEmail: user?.email,
          applicationId: id,
          componentName: 'ApplicationProcessing',
          functionName: 'handleApprove'
        });
        throw new Error(error?.message || error?.toString() || 'Failed to approve application');
      }

      // Refresh data
      await fetchApplicationsData();
    } catch (error) {
      console.error('Error approving application:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
    }
  };

  const handleReject = async (id) => {
    try {
      const { error } = await supabase?.from('applications')?.update({ application_status: 'rejected' })?.eq('id', id);

      if (error) {
        // Log error to centralized system
        await logSupabaseError({
          error,
          query: 'handleReject',
          table: 'applications',
          operation: 'UPDATE',
          userEmail: user?.email,
          applicationId: id,
          componentName: 'ApplicationProcessing',
          functionName: 'handleReject'
        });
        throw new Error(error?.message || error?.toString() || 'Failed to reject application');
      }

      // Refresh data
      await fetchApplicationsData();
    } catch (error) {
      console.error('Error rejecting application:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
    }
  };

  const handleApproveSelected = async () => {
    try {
      // Log bulk action
      await logBulkAction({
        actionType: 'approve',
        entityType: 'application',
        entityIds: selectedIds,
        userEmail: user?.email || 'admin@gridbalance.de',
        description: `Bulk approved ${selectedIds?.length} applications`,
        metadata: {
          application_count: selectedIds?.length,
          action: 'bulk_approve'
        }
      });
      
      selectedIds?.forEach((id) => handleApprove(id));
      setSelectedIds([]);
    } catch (error) {
      console.error('Error in bulk approve:', error);
    }
  };

  const handleRejectSelected = async () => {
    try {
      // Log bulk action
      await logBulkAction({
        actionType: 'reject',
        entityType: 'application',
        entityIds: selectedIds,
        userEmail: user?.email || 'admin@gridbalance.de',
        description: `Bulk rejected ${selectedIds?.length} applications`,
        metadata: {
          application_count: selectedIds?.length,
          action: 'bulk_reject'
        }
      });
      
      selectedIds?.forEach((id) => handleReject(id));
      setSelectedIds([]);
    } catch (error) {
      console.error('Error in bulk reject:', error);
    }
  };

  const handleSendEmails = () => {
    setEmailModal({ isOpen: true });
  };

  const handleEmailSend = (emailData) => {
    console.log('Sending emails to:', selectedIds, 'with data:', emailData);
    setSelectedIds([]);
  };

  const handleUpdateChassisNumber = async (id, newChassisNumber) => {
    try {
      const { error } = await supabase?.from('applications')?.update({ chassis_number: newChassisNumber })?.eq('id', id);

      if (error) {
        // Log error to centralized system
        await logSupabaseError({
          error,
          query: 'handleUpdateChassisNumber',
          table: 'applications',
          operation: 'UPDATE',
          userEmail: user?.email,
          applicationId: id,
          componentName: 'ApplicationProcessing',
          functionName: 'handleUpdateChassisNumber',
          additionalMetadata: { newChassisNumber }
        });
        throw new Error(error?.message || error?.toString() || 'Failed to update chassis number');
      }

      // Refresh data
      await fetchApplicationsData();
    } catch (error) {
      console.error('Error updating chassis number:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
    }
  };

  const generateVoucherCode = () => {
    const parts = [];
    for (let i = 0; i < 3; i++) {
      let part = '';
      for (let j = 0; j < 4; j++) {
        part += Math.floor(Math.random() * 10)?.toString();
      }
      parts?.push(part);
    }
    return parts?.join('-');
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      status: 'all',
      dealership: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  const handleStatusUpdate = async (applicationId, newStatus, notes = '') => {
    try {
      const application = applications?.find((app) => app?.id === applicationId);

      // Get the old status before updating
      const oldStatus = application?.status;

      // Client-side validation of status transitions
      const allowedTransitions = {
        'unbestätigt': ['processing'],
        'bestätigt': ['processing'],
        'processing': ['approved', 'rejected'],
        'approved': ['redeemed'],
        'rejected': [],
        'redeemed': []
      };

      const allowed = allowedTransitions?.[oldStatus] || [];
      
      if (!allowed?.includes(newStatus)) {
        alert(
          `Ungültiger Statusübergang!\n\n` +
          `Aktueller Status: "${oldStatus}"\n` +
          `Versuchter Status: "${newStatus}"\n\n` +
          `Erlaubte Übergänge:\n` +
          `• unbestätigt → beantragt\n` +
          `• bestätigt → beantragt\n` +
          `• beantragt → genehmigt/abgelehnt\n` +
          `• genehmigt → eingelöst\n\n` +
          `Hinweis: Abgelehnte und eingelöste Anträge sind Endstatus und können nicht geändert werden.`
        );
        return;
      }

      // Update in Supabase
      const { error } = await supabase?.from('applications')?.update({ 
          application_status: newStatus,
          notes: notes,
          updated_at: new Date()?.toISOString()
        })?.eq('id', applicationId);

      if (error) {
        // Check if error is due to status transition validation
        if (error?.message && error?.message?.includes('can only transition')) {
          alert(
            `Statusübergang nicht erlaubt!\n\n` +
            `${error?.message}\n\n` +
            `Bitte folgen Sie dem korrekten Workflow:\n` +
            `unbestätigt → bestätigt → beantragt → genehmigt/abgelehnt → eingelöst`
          );
          return;
        }
        
        // Log error to centralized system
        await logSupabaseError({
          error,
          query: 'handleStatusUpdate',
          table: 'applications',
          operation: 'UPDATE',
          userEmail: user?.email,
          applicationId: applicationId,
          componentName: 'ApplicationProcessing',
          functionName: 'handleStatusUpdate',
          additionalMetadata: { oldStatus, newStatus, notes }
        });
        throw new Error(error?.message || error?.toString() || 'Failed to update application status');
      }

      // Send status-specific emails based on status transitions
      // CRITICAL: NEVER send emails for 'unbestätigt' status (registration handles this)
      if (application?.email && oldStatus !== 'unbestätigt' && newStatus !== 'unbestätigt') {
        try {
          // Status: bestätigt -> processing (Antrag bei Behörden eingereicht)
          if (oldStatus === 'bestätigt' && newStatus === 'processing') {
            // Fetch full application data including license plate
            const { data: fullAppData, error: fetchError } = await supabase
              ?.from('applications')
              ?.select('customer_license_plate, vehicle_model')
              ?.eq('id', applicationId)
              ?.single();

            if (fetchError) {
              console.error('Error fetching application data for email:', fetchError);
            }

            console.log('[handleStatusUpdate] Sending application submitted email to:', application?.email);
            const emailResult = await sendApplicationSubmittedEmail({
              customerEmail: application?.email,
              customerName: application?.customerName,
              dealershipName: application?.dealershipName,
              chassisNumber: application?.chassisNumber,
              applicationId: applicationId,
              customerLicensePlate: fullAppData?.customer_license_plate || 'Nicht angegeben',
              vehicleModel: fullAppData?.vehicle_model || application?.vehicleModel
            });
            
            if (emailResult?.success) {
              console.log('[handleStatusUpdate] ✅ Application submitted email sent successfully');
              alert(`✅ Status erfolgreich auf "Beantragt" aktualisiert!\n\n📧 Informations-E-Mail wurde an ${application?.email} gesendet.\n\nDer Kunde wurde informiert, dass sein Antrag bei den Behörden eingereicht wurde.`);
            } else {
              console.error('[handleStatusUpdate] ❌ Failed to send application submitted email:', emailResult?.error);
              alert(`⚠️ Status wurde auf "Beantragt" aktualisiert, aber E-Mail konnte nicht gesendet werden.\n\nFehler: ${emailResult?.error || 'Unbekannter Fehler'}\n\nBitte prüfen Sie die SMTP-Konfiguration.`);
            }
            
            // Log email sent
            await logEmailSent({
              to: application?.email,
              subject: 'Antrag bei Behörden eingereicht',
              templateType: 'application_submitted',
              applicationId: applicationId,
              dealershipId: application?.dealershipId,
              userEmail: user?.email || 'admin@gridbalance.de'
            });
          }
          
          // Status: ANY -> approved (Antrag genehmigt) - FIXED: Send email regardless of old status
          else if (newStatus === 'approved' && oldStatus !== 'approved') {
            // Fetch full application data including license plate
            const { data: fullAppData, error: fetchError } = await supabase
              ?.from('applications')
              ?.select('customer_license_plate, dealership_id')
              ?.eq('id', applicationId)
              ?.single();

            if (fetchError) {
              console.error('Error fetching application data for email:', fetchError);
            }

            console.log('[handleStatusUpdate] ========== STARTING APPROVED EMAIL WORKFLOW ==========');
            console.log('[handleStatusUpdate] Application ID:', applicationId);
            console.log('[handleStatusUpdate] Customer Email:', application?.email);
            console.log('[handleStatusUpdate] Dealership ID from query:', fullAppData?.dealership_id);
            console.log('[handleStatusUpdate] Dealership ID from application:', application?.dealershipId);
            console.log('[handleStatusUpdate] Old Status:', oldStatus, '-> New Status:', newStatus);
            
            try {
              const emailResult = await sendApplicationApprovedEmail({
                id: applicationId,
                customerEmail: application?.email,
                customerName: application?.customerName,
                dealershipName: application?.dealershipName,
                chassisNumber: application?.chassisNumber,
                applicationId: applicationId,
                dealershipId: fullAppData?.dealership_id || application?.dealershipId,
                customerLicensePlate: fullAppData?.customer_license_plate || 'Nicht angegeben'
              });
              
              console.log('[handleStatusUpdate] Email Result:', JSON.stringify(emailResult, null, 2));
              
              if (emailResult?.success) {
                console.log('[handleStatusUpdate] ✅ ✅ ✅ APPROVED EMAIL SENT SUCCESSFULLY ✅ ✅ ✅');
                console.log('[handleStatusUpdate] Voucher Code:', emailResult?.voucherCode);
                alert(`✅ Status erfolgreich auf "Genehmigt" aktualisiert!\n\n📧 E-Mail mit Gutscheincode wurde an ${application?.email} gesendet.\n\n🎟️ Gutscheincode: ${emailResult?.voucherCode || 'Siehe E-Mail'}`);
              } else {
                console.error('[handleStatusUpdate] ❌ ❌ ❌ FAILED TO SEND APPROVED EMAIL ❌ ❌ ❌');
                console.error('[handleStatusUpdate] Error:', emailResult?.error);
                alert(`⚠️ Status wurde auf "Genehmigt" aktualisiert, aber E-Mail konnte nicht gesendet werden.\n\nFehler: ${emailResult?.error || 'Unbekannter Fehler'}\n\nBitte prüfen Sie:\n1. GMAIL_APP_PASSWORD in Supabase Secrets\n2. Edge Function Deployment\n3. Email Template in Datenbank`);
              }
            } catch (emailError) {
              console.error('[handleStatusUpdate] ❌ ❌ ❌ EXCEPTION SENDING APPROVED EMAIL ❌ ❌ ❌');
              console.error('[handleStatusUpdate] Exception:', emailError);
              alert(`⚠️ Status wurde auf "Genehmigt" aktualisiert, aber E-Mail konnte nicht gesendet werden.\n\nAusnahme: ${emailError?.message || emailError}\n\nBitte prüfen Sie die Browser-Konsole für Details.`);
            }
            
            // Log email sent
            await logEmailSent({
              to: application?.email,
              subject: 'Antrag genehmigt',
              templateType: 'application_approved',
              applicationId: applicationId,
              dealershipId: application?.dealershipId,
              userEmail: user?.email || 'admin@gridbalance.de'
            });
          }
          
          // Status: ANY -> rejected (Antrag abgelehnt) - FIXED: Send email regardless of old status
          else if (newStatus === 'rejected' && oldStatus !== 'rejected') {
            // Fetch full application data including license plate
            const { data: fullAppData, error: fetchError } = await supabase
              ?.from('applications')
              ?.select('customer_license_plate')
              ?.eq('id', applicationId)
              ?.single();

            if (fetchError) {
              console.error('Error fetching application data for email:', fetchError);
            }

            console.log('[handleStatusUpdate] Sending rejected email to:', application?.email);
            const emailResult = await sendApplicationRejectedEmail({
              id: applicationId,
              customerEmail: application?.email,
              customerName: application?.customerName,
              dealershipName: application?.dealershipName,
              chassisNumber: application?.chassisNumber,
              rejectionReason: notes || 'Keine Angabe',
              applicationId: applicationId,
              customerLicensePlate: fullAppData?.customer_license_plate || 'Nicht angegeben'
            });
            
            if (emailResult?.success) {
              console.log('[handleStatusUpdate] ✅ Rejected email sent successfully');
              alert(`Status erfolgreich aktualisiert und E-Mail an ${application?.email} gesendet.`);
            } else {
              console.error('[handleStatusUpdate] ❌ Failed to send rejected email:', emailResult?.error);
              alert(`⚠️ Status wurde aktualisiert, aber E-Mail konnte nicht gesendet werden: ${emailResult?.error || 'Unbekannter Fehler'}. Bitte prüfen Sie die SMTP-Konfiguration.`);
            }
            
            // Log email sent
            await logEmailSent({
              to: application?.email,
              subject: 'Antrag abgelehnt',
              templateType: 'application_rejected',
              applicationId: applicationId,
              dealershipId: application?.dealershipId,
              userEmail: user?.email || 'admin@gridbalance.de'
            });
          }
          // Status: any -> redeemed (Gutschein eingelöst)
          else if (newStatus === 'redeemed') {
            const adminNotificationResult = await sendAdminRedeemedNotification({
              applicationId: applicationId,
              customerName: application?.customerName,
              customerEmail: application?.email,
              dealershipName: application?.dealershipName || 'Unbekannt',
              voucherCode: application?.voucherCode || null,
              chassisNumber: application?.chassisNumber || null
            });
            
            if (adminNotificationResult?.success) {
              console.log('Admin redeemed notification sent successfully');
            } else {
              console.error('Failed to send admin redeemed notification:', adminNotificationResult?.error);
            }
          }
          // CRITICAL FIX: Do NOT send generic status update emails for:
          // - Any transition involving 'unbestätigt' (handled by registration flow)
          // - Transition from 'unbestätigt' to 'bestätigt' (handled by confirmation page)
          // Only send for other unexpected status changes
          else if (oldStatus !== 'bestätigt' || newStatus === 'bestätigt') {
            // Skip email - these transitions are handled by specific flows
            console.log(`Skipping generic status update email for ${oldStatus} -> ${newStatus} transition`);
          } else {
            // For any other status changes, send generic status update
            await sendStatusUpdate({
              customerEmail: application?.email,
              dealershipName: application?.dealershipName || 'Valued Partner',
              status: newStatus,
              applicationId: applicationId,
              notes: notes,
              message: `Your application has been ${newStatus}.`
            });
          }
        } catch (emailError) {
          console.error('Error sending status change email:', emailError);
          // Don't throw - status was already updated successfully
        }
      } else if (oldStatus === 'unbestätigt' || newStatus === 'unbestätigt') {
        console.log('Skipping email for unbestätigt status - handled by registration/confirmation flow');
      }

      // Refresh data for non-approved status changes
      await fetchApplicationsData();
    } catch (error) {
      console.error('Failed to update status:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
      alert('Failed to update application status');
    }
  };

  const handleBulkAction = async (action) => {
    const selectedApplications = applications?.filter((app) => selectedIds?.includes(app?.id));
    
    // Check current status of selected applications
    const currentStatuses = [...new Set(selectedApplications?.map(app => app?.status))];
    
    if (action === 'weiter') {
      // For "bestätigt" (confirmed) status -> download vehicle documents as ZIP
      if (currentStatuses?.includes('bestätigt')) {
        try {
          // Check if user is authenticated
          const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
          
          if (sessionError || !session) {
            alert('Fehler: Sie müssen angemeldet sein, um Dokumente herunterzuladen.');
            console.warn('Authentication check failed:', sessionError?.message || 'No session found');
            return;
          }

          // Filter applications that have vehicle documents
          const appsWithDocs = selectedApplications?.filter(app => app?.documentUrl);
          
          if (appsWithDocs?.length === 0) {
            alert('Keine Fahrzeugscheine zum Herunterladen gefunden. Bitte wählen Sie Vorgänge mit hochgeladenen Dokumenten aus.');
            return;
          }

          // Create ZIP file
          const zip = new JSZip();
          let successCount = 0;
          let errorCount = 0;
          const errorDetails = [];

          // Download each document and add to ZIP
          for (const app of appsWithDocs) {
            try {
              // Extract file path from storage URL
              let filePath = null;
              
              // Try to extract path after 'vehicle-documents/'
              const vehicleDocsIndex = app?.documentUrl?.indexOf('vehicle-documents/');
              if (vehicleDocsIndex !== -1) {
                // Extract everything after 'vehicle-documents/'
                filePath = app?.documentUrl?.substring(vehicleDocsIndex + 'vehicle-documents/'?.length);
                // Remove any query parameters
                filePath = filePath?.split('?')?.[0];
              }
              
              if (!filePath) {
                const errorMsg = `Ungültige Dokument-URL für ${app?.customerName}`;
                console.error(errorMsg, 'URL:', app?.documentUrl);
                errorDetails?.push({ customer: app?.customerName, error: 'Ungültige URL' });
                errorCount++;
                continue;
              }

              console.log(`Downloading document for ${app?.customerName}, path: ${filePath}`);

              // Use download method for authenticated access
              const { data: fileData, error: downloadError } = await supabase?.storage
                ?.from('vehicle-documents')
                ?.download(filePath);

              // Detailed error logging
              if (downloadError) {
                console.error(`Download error for ${app?.customerName}:`, downloadError);
                errorDetails?.push({
                  customer: app?.customerName,
                  error: downloadError?.message || 'Download fehlgeschlagen'
                });
                errorCount++;
                continue;
              }

              if (!fileData) {
                console.error(`No file data returned for ${app?.customerName}`);
                errorDetails?.push({ customer: app?.customerName, error: 'Keine Datei gefunden' });
                errorCount++;
                continue;
              }

              // fileData is already a Blob, validate it
              if (fileData?.size === 0) {
                console.error(`Empty document for ${app?.customerName}`);
                errorDetails?.push({ customer: app?.customerName, error: 'Leeres Dokument' });
                errorCount++;
                continue;
              }

              // Generate filename: CustomerName_ChassisNumber.extension
              const fileExtension = filePath?.split('.')?.pop() || 'pdf';
              const safeCustomerName = app?.customerName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Unknown';
              const safeChassisNumber = app?.chassisNumber?.replace(/[^a-zA-Z0-9]/g, '_') || 'NoVIN';
              const filename = `${safeCustomerName}_${safeChassisNumber}.${fileExtension}`;

              // Add file to ZIP with error handling
              try {
                zip?.file(filename, fileData);
                successCount++;
                console.log(`Successfully added ${filename} to ZIP`);
              } catch (zipError) {
                console.error(`Failed to add ${filename} to ZIP:`, zipError);
                errorDetails?.push({ 
                  customer: app?.customerName, 
                  error: 'Fehler beim Hinzufügen zur ZIP-Datei' 
                });
                errorCount++;
              }
            } catch (err) {
              console.error(`Unexpected error processing document for ${app?.customerName}:`, err instanceof Error ? err : new Error(JSON.stringify({
                message: err?.message || 'Unknown error',
                stack: err?.stack,
                error: String(err)
              })));
              errorDetails?.push({ 
                customer: app?.customerName, 
                error: err?.message || 'Unbekannter Fehler' 
              });
              errorCount++;
            }
          }

          if (successCount === 0) {
            // Show detailed error message
            let errorMessage = 'Fehler: Keine Fahrzeugscheine konnten heruntergeladen werden.\n\n';
            if (errorDetails?.length > 0) {
              errorMessage += 'Details:\n';
              errorDetails?.forEach(detail => {
                errorMessage += `- ${detail?.customer}: ${detail?.error}\n`;
              });
            }
            alert(errorMessage);
            return;
          }

          // Generate ZIP file
          const zipBlob = await zip?.generateAsync({ type: 'blob' });
          
          // Create download link
          const downloadUrl = URL.createObjectURL(zipBlob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `Fahrzeugscheine_${new Date()?.toISOString()?.split('T')?.[0]}.zip`;
          document.body?.appendChild(link);
          link?.click();
          document.body?.removeChild(link);
          URL.revokeObjectURL(downloadUrl);

          // Show success message with error details if any
          let message = `${successCount} Fahrzeugschein(e) erfolgreich heruntergeladen.`;
          if (errorCount > 0) {
            message += `\n\n${errorCount} Fehler aufgetreten:`;
            errorDetails?.forEach(detail => {
              message += `\n- ${detail?.customer}: ${detail?.error}`;
            });
          }
          alert(message);
          
          // Calculate current status before opening modal
          const modalCurrentStatus = currentStatuses?.length === 1 ? currentStatuses?.[0] : null;
          // Show status selection modal after successful download
          setStatusSelectionModal({ isOpen: true, action: 'pending_to_status', currentStatus: modalCurrentStatus });
        } catch (error) {
          console.error('Error creating ZIP file:', error instanceof Error ? error : new Error(JSON.stringify({
            message: error?.message || 'Unknown error',
            stack: error?.stack,
            error: String(error)
          })));
          alert(`Fehler beim Erstellen der ZIP-Datei: ${error?.message || 'Unbekannter Fehler'}`);
        }
      }
      // For "beantragt" (processing) status -> show status selection modal
      else if (currentStatuses?.includes('processing')) {
        const modalCurrentStatus = currentStatuses?.length === 1 ? currentStatuses?.[0] : null;
        setStatusSelectionModal({ isOpen: true, action: 'processing_to_final', currentStatus: modalCurrentStatus });
      }
      // For any status -> show status selection modal
      else {
        const modalCurrentStatus = currentStatuses?.length === 1 ? currentStatuses?.[0] : null;
        setStatusSelectionModal({ isOpen: true, action: 'general_status_change', currentStatus: modalCurrentStatus });
      }
    }
  };

  const handleStatusSelection = async (selectedStatus, notes) => {
    const selectedApplications = applications?.filter((app) => selectedIds?.includes(app?.id));
    
    // Update all selected applications to the chosen status
    await Promise.all(selectedApplications?.map(async (app) => {
      await handleStatusUpdate(app?.id, selectedStatus, notes);
    }));
    
    setSelectedIds([]);
    setStatusSelectionModal({ isOpen: false, action: null, currentStatus: null });
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      console.log('[handleStatusChange] ========== START ==========');
      console.log('[handleStatusChange] Application ID:', applicationId);
      console.log('[handleStatusChange] New Status:', newStatus);
      
      const application = applications?.find((app) => app?.id === applicationId);
      
      if (!application) {
        throw new Error('Application not found');
      }

      console.log('[handleStatusChange] Application found:', {
        id: application?.id,
        customerName: application?.customerName,
        currentStatus: application?.status,
        dealershipId: application?.dealershipId,
        email: application?.email
      });

      // Get the old status before updating
      const oldStatus = application?.status;
      const status = newStatus;

      console.log('[handleStatusChange] Attempting Supabase update...');
      console.log('[handleStatusChange] Update query:', {
        table: 'applications',
        update: { application_status: status },
        where: { id: applicationId }
      });

      // Update status in database
      const { data: updateData, error } = await supabase?.from('applications')?.update({ application_status: status })?.eq('id', applicationId);

      console.log('[handleStatusChange] Supabase response:', {
        data: updateData,
        error: error,
        errorMessage: error?.message,
        errorDetails: error?.details,
        errorHint: error?.hint,
        errorCode: error?.code
      });

      if (error) {
        console.error('[handleStatusChange] ❌ SUPABASE UPDATE FAILED');
        console.error('[handleStatusChange] Error object:', JSON.stringify(error, null, 2));
        console.error('[handleStatusChange] Error message:', error?.message);
        console.error('[handleStatusChange] Error details:', error?.details);
        console.error('[handleStatusChange] Error hint:', error?.hint);
        console.error('[handleStatusChange] Error code:', error?.code);
        
        // Log to error logging system
        await logSupabaseError({
          error,
          query: 'handleStatusChange - status update',
          table: 'applications',
          operation: 'UPDATE',
          userEmail: user?.email,
          applicationId: applicationId,
          componentName: 'ApplicationProcessing',
          functionName: 'handleStatusChange',
          additionalMetadata: { oldStatus, newStatus: status }
        });
        
        throw error;
      }

      console.log('[handleStatusChange] ✅ Status updated successfully in database');

      // Send status-specific emails based on status transitions
      if (application?.email) {
        try {
          // Status: bestätigt -> processing (Antrag genehmigt mit Gutscheincode)
          if (oldStatus === 'bestätigt' && status === 'processing') {
            console.log('[handleStatusChange] ========== PROCESSING EMAIL WORKFLOW START ==========');
            // Fetch full application data including license plate
            const { data: fullAppData, error: fetchError } = await supabase
              ?.from('applications')
              ?.select('customer_license_plate, vehicle_model')
              ?.eq('id', applicationId)
              ?.single();

            if (fetchError) {
              console.error('[handleStatusChange] Error fetching application data for email:', fetchError);
              await logSupabaseError({
                error: fetchError,
                query: 'handleStatusChange - fetch application data',
                table: 'applications',
                operation: 'SELECT',
                userEmail: user?.email,
                applicationId: applicationId,
                componentName: 'ApplicationProcessing',
                functionName: 'handleStatusChange'
              });
            }
            
            console.log('[handleStatusChange] Sending application submitted email');

            const emailResult = await sendApplicationSubmittedEmail({
              customerEmail: application?.email,
              customerName: application?.customerName,
              dealershipName: application?.dealershipName,
              chassisNumber: application?.chassisNumber,
              applicationId: applicationId,
              customerLicensePlate: fullAppData?.customer_license_plate || 'Nicht angegeben'
            });
            
            if (emailResult?.success) {
              console.log('[handleStatusChange] ✅ Application submitted email sent successfully');
            } else {
              console.error('[handleStatusChange] ❌ Failed to send application submitted email:', emailResult?.error);
              alert(`Fehler beim Senden der E-Mail: ${emailResult?.error || 'Unbekannter Fehler'}. Bitte prüfen Sie die Konsole für Details.`);
            }
            console.log('[handleStatusChange] ========== PROCESSING EMAIL WORKFLOW END ==========');
          }
          // Status: processing -> approved (Antrag genehmigt mit Gutscheincode)
          else if (oldStatus === 'processing' && status === 'approved') {
            console.log('[handleStatusChange] ========== APPROVAL WORKFLOW START ==========');
            console.log('[handleStatusChange] Step 1: Fetching full application data...');
            
            // Fetch full application data including license plate AND dealership_id
            const { data: fullAppData, error: fetchError } = await supabase
              ?.from('applications')
              ?.select('customer_license_plate, vehicle_model, dealership_id')
              ?.eq('id', applicationId)
              ?.single();

            console.log('[handleStatusChange] Step 1 Result:', {
              success: !fetchError,
              data: fullAppData,
              error: fetchError
            });

            if (fetchError) {
              console.error('[handleStatusChange] ❌ STEP 1 FAILED: Error fetching application data');
              console.error('[handleStatusChange] Fetch error:', JSON.stringify(fetchError, null, 2));
              
              await logSupabaseError({
                error: fetchError,
                query: 'handleStatusChange - fetch application data for approval',
                table: 'applications',
                operation: 'SELECT',
                userEmail: user?.email,
                applicationId: applicationId,
                componentName: 'ApplicationProcessing',
                functionName: 'handleStatusChange'
              });
              
              alert(`Fehler beim Abrufen der Antragsdaten: ${fetchError?.message}. Bitte versuchen Sie es erneut.`);
              return;
            }

            if (!fullAppData?.dealership_id) {
              console.error('[handleStatusChange] ❌ STEP 1 FAILED: dealership_id is missing from database');
              console.error('[handleStatusChange] Full application data:', JSON.stringify(fullAppData, null, 2));
              alert('Fehler: Händler-ID fehlt in den Antragsdaten. Bitte kontaktieren Sie den Support.');
              return;
            }

            console.log('[handleStatusChange] ✅ Step 1 SUCCESS: Application data fetched');
            console.log('[handleStatusChange] Step 2: Preparing email data...');

            const emailData = {
              id: applicationId,
              customerEmail: application?.email,
              customerName: application?.customerName,
              dealershipName: application?.dealershipName || 'Ihr Partner',
              dealershipId: fullAppData?.dealership_id,
              customerLicensePlate: fullAppData?.customer_license_plate || 'Nicht angegeben'
            };

            console.log('[handleStatusChange] Step 2: Email data prepared:', JSON.stringify(emailData, null, 2));
            console.log('[handleStatusChange] Step 3: Calling sendApplicationApprovedEmail...');

            const emailResult = await sendApplicationApprovedEmail(emailData);

            console.log('[handleStatusChange] Step 3 Result:', {
              success: emailResult?.success,
              voucherCode: emailResult?.voucherCode,
              error: emailResult?.error
            });

            if (emailResult?.success) {
              console.log('[handleStatusChange] ✅ APPROVAL WORKFLOW SUCCESS');
              console.log('[handleStatusChange] Voucher code:', emailResult?.voucherCode);
              alert(`Antrag genehmigt! Gutscheincode generiert: ${emailResult?.voucherCode}`);
              // Refresh data after voucher creation to show voucher code in grid
              await fetchApplicationsData();
            } else {
              console.error('[handleStatusChange] ❌ APPROVAL WORKFLOW FAILED AT EMAIL STEP');
              console.error('[handleStatusChange] Email error:', emailResult?.error);
              
              // Log email error
              await logSupabaseError({
                error: new Error(emailResult?.error || 'Unknown email error'),
                query: 'handleStatusChange - send approval email',
                table: 'applications',
                operation: 'EMAIL',
                userEmail: user?.email,
                applicationId: applicationId,
                componentName: 'ApplicationProcessing',
                functionName: 'handleStatusChange',
                additionalMetadata: { emailData }
              });
              
              alert(`Fehler beim Senden der E-Mail: ${emailResult?.error || 'Unbekannter Fehler'}. Bitte prüfen Sie die Konsole für Details.`);
              // Still refresh data even if email failed, in case voucher was created
              await fetchApplicationsData();
            }
            console.log('[handleStatusChange] ========== APPROVAL WORKFLOW END ==========');
            return; // Exit early since we already refreshed data
          }
          // Status: any -> rejected (Antrag abgelehnt)
          else if (status === 'rejected') {
            console.log('[handleStatusChange] ========== REJECTION EMAIL WORKFLOW START ==========');
            const emailResult = await sendApplicationRejectedEmail({
              id: applicationId,
              customerEmail: application?.email,
              customerName: application?.customerName,
              dealershipName: application?.dealershipName,
              rejectionReason: application?.notes || ''
            });
            
            if (emailResult?.success) {
              console.log('[handleStatusChange] ✅ Application rejected email sent successfully');
            } else {
              console.error('[handleStatusChange] ❌ Failed to send application rejected email:', emailResult?.error);
            }
            console.log('[handleStatusChange] ========== REJECTION EMAIL WORKFLOW END ==========');
          }
          // Status: any -> redeemed (Gutschein eingelöst)
          else if (status === 'redeemed') {
            console.log('[handleStatusChange] ========== REDEEMED NOTIFICATION WORKFLOW START ==========');
            const adminNotificationResult = await sendAdminRedeemedNotification({
              applicationId: applicationId,
              customerName: application?.customerName,
              customerEmail: application?.email,
              dealershipName: application?.dealershipName || 'Unbekannt',
              voucherCode: application?.voucherCode || null,
              chassisNumber: application?.chassisNumber || null
            });
            
            if (adminNotificationResult?.success) {
              console.log('[handleStatusChange] ✅ Admin redeemed notification sent successfully');
            } else {
              console.error('[handleStatusChange] ❌ Failed to send admin redeemed notification:', adminNotificationResult?.error);
            }
            console.log('[handleStatusChange] ========== REDEEMED NOTIFICATION WORKFLOW END ==========');
          }
        } catch (emailError) {
          console.error('[handleStatusChange] ❌ EXCEPTION IN EMAIL WORKFLOW');
          console.error('[handleStatusChange] Email exception:', emailError);
          console.error('[handleStatusChange] Email exception message:', emailError?.message);
          console.error('[handleStatusChange] Email exception stack:', emailError?.stack);
          
          // Log email exception
          await logSupabaseError({
            error: emailError,
            query: 'handleStatusChange - email workflow exception',
            table: 'applications',
            operation: 'EMAIL',
            userEmail: user?.email,
            applicationId: applicationId,
            componentName: 'ApplicationProcessing',
            functionName: 'handleStatusChange',
            additionalMetadata: { oldStatus, newStatus: status }
          });
          
          // Don't throw - status was already updated successfully
          alert(`Status wurde aktualisiert, aber E-Mail-Versand schlug fehl: ${emailError?.message}`);
        }
      }

      // Refresh applications data for non-approved status changes
      console.log('[handleStatusChange] Refreshing applications data...');
      await fetchApplicationsData();
      
      console.log('[handleStatusChange] ========== END (SUCCESS) ==========');
    } catch (error) {
      console.error('[handleStatusChange] ========== END (ERROR) ==========');
      console.error('[handleStatusChange] Error type:', error?.constructor?.name);
      console.error('[handleStatusChange] Error message:', error?.message);
      console.error('[handleStatusChange] Error details:', error?.details);
      console.error('[handleStatusChange] Error hint:', error?.hint);
      console.error('[handleStatusChange] Error code:', error?.code);
      console.error('[handleStatusChange] Full error object:', JSON.stringify(error, null, 2));
      console.error('[handleStatusChange] Stack trace:', error?.stack);
      
      // Log to error logging system
      await logSupabaseError({
        error,
        query: 'handleStatusChange - main catch block',
        table: 'applications',
        operation: 'UPDATE',
        userEmail: user?.email,
        applicationId: applicationId,
        componentName: 'ApplicationProcessing',
        functionName: 'handleStatusChange',
        additionalMetadata: { newStatus }
      });
      
      // Show the ACTUAL error message to the user
      let errorMessage = error?.message || error?.details || error?.hint || 'Unbekannter Fehler';
      
      // Check if it's a trigger validation error
      if (errorMessage?.includes('can only transition') || errorMessage?.includes('can transition')) {
        alert(`Statusübergang nicht erlaubt!\n\n${errorMessage}\n\nBitte folgen Sie dem korrekten Workflow.`);
      } else {
        alert(`Fehler beim Aktualisieren des Status:\n\n${errorMessage}\n\nBitte prüfen Sie die Browser-Konsole für Details.`);
      }
    }
  };

  const handleConfirmVoucher = async (applicationId) => {
    try {
      setIsConfirming(true);

      // First check if already redeemed
      const { data: existingApp, error: checkError } = await supabase
        ?.from('applications')
        ?.select('application_status, redeemed_at')
        ?.eq('id', applicationId)
        ?.single();

      if (checkError) {
        throw new Error(checkError?.message || 'Fehler beim Prüfen des Status');
      }

      if (existingApp?.application_status === 'redeemed') {
        const redemptionDate = existingApp?.redeemed_at
          ? new Date(existingApp?.redeemed_at)?.toLocaleDateString('de-DE', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Datum unbekannt';
        
        alert(`Dieser Gutschein wurde bereits eingelöst am ${redemptionDate}`);
        setIsConfirming(false);
        return;
      }

      // Update application status to redeemed with timestamp
      const redemptionTimestamp = new Date()?.toISOString();
      const { error: updateError } = await supabase
        ?.from('applications')
        ?.update({ 
          application_status: 'redeemed',
          redeemed_at: redemptionTimestamp,
          updated_at: redemptionTimestamp
        })
        ?.eq('id', applicationId);
    } catch (error) {
      console.error('Error confirming voucher:', error instanceof Error ? error : new Error(String(error || 'Unknown error')));
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleOpenLogger = (application) => {
    setSelectedApplicationForLogger(application);
    setIsLoggerOpen(true);
  };

  const handleCloseLogger = () => {
    setIsLoggerOpen(false);
    setSelectedApplicationForLogger(null);
  };

  const handleSelectApplication = (applicationId) => {
    setSelectedApplicationIds((prev) =>
      prev?.includes(applicationId) 
        ? prev?.filter((id) => id !== applicationId) 
        : [...prev, applicationId]
    );
  };

  const handleViewDocument = (application) => {
    // Log document view for audit trail
    if (application?.documentUrl) {
      logDocumentView({
        fileName: application?.documentUrl?.split('/')?.pop() || 'vehicle_document',
        applicationId: application?.id,
        dealershipId: application?.dealershipId,
        userEmail: user?.email || 'admin@gridbalance.de'
      });
    }
    setDocumentModal({ isOpen: true, application });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Anträge...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`transition-layout ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
        <header className="bg-card border-b border-border sticky top-0 z-20">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  Antragsbearbeitung
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Verwalten und bearbeiten Sie THG-Prämienanträge
                </p>
              </div>
              <Button
                variant="default"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}>

                Aktualisieren
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ProcessingStats stats={stats} />

          <ApplicationFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            dealerships={dealerships} />


          <div className="bg-card rounded-lg shadow-card overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    Anträge
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {filteredApplications?.length} {filteredApplications?.length === 1 ? 'Antrag' : 'Anträge'} gefunden
                  </p>
                </div>
                {selectedIds?.length > 0 &&
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {selectedIds?.length} ausgewählt
                    </span>
                  </div>
                }
              </div>
            </div>

            <ApplicationTable
              applications={filteredApplications}
              selectedIds={selectedIds}
              onSelectAll={handleSelectAll}
              onSelectOne={handleSelectOne}
              onViewDocument={(app) => setDocumentModal({ isOpen: true, application: app })}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewDetails={(app) => setDetailsModal({ isOpen: true, application: app })}
              onStatusChange={handleStatusChange}
              selectedApplicationIds={selectedApplicationIds}
              onSelectApplication={handleSelectApplication}
              onOpenLogger={handleOpenLogger}
            />

          </div>
        </main>
      </div>
      {selectedIds?.length > 0 &&
      <BulkActionsBar
        selectedCount={selectedIds?.length}
        onClearSelection={handleClearSelection}
        onBulkAction={handleBulkAction}
        onBulkEmail={() => setIsBulkEmailModalOpen(true)}
        selectedApplications={applications?.filter((app) => selectedIds?.includes(app?.id))} />

      }
      <DocumentViewerModal
        isOpen={documentModal?.isOpen}
        onClose={() => setDocumentModal({ isOpen: false, application: null })}
        application={documentModal?.application}
        onUpdateChassisNumber={handleUpdateChassisNumber} />
      <ApplicationDetailsModal
        isOpen={detailsModal?.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, application: null })}
        application={detailsModal?.application}
        onApprove={handleApprove}
        onReject={handleReject} />
      <EmailComposerModal
        isOpen={emailModal?.isOpen}
        onClose={() => setEmailModal({ isOpen: false })}
        selectedApplications={applications?.filter((app) => selectedIds?.includes(app?.id))}
        onSend={handleEmailSend} />
      {/* Bulk Email Modal */}
      <BulkEmailComposerModal
        isOpen={isBulkEmailModalOpen}
        onClose={() => setIsBulkEmailModalOpen(false)}
        selectedApplications={applications?.filter((app) =>
        selectedIds?.includes(app?.id)
        )} />
      {/* Status Selection Modal */}
      <StatusSelectionModal
        isOpen={statusSelectionModal?.isOpen}
        onClose={() => setStatusSelectionModal({ isOpen: false, action: null, currentStatus: null })}
        onConfirm={handleStatusSelection}
        currentStatus={statusSelectionModal?.currentStatus} />
      <DatabaseQueryLogger
        applicationId={selectedApplicationForLogger?.id}
        isOpen={isLoggerOpen}
        onClose={handleCloseLogger}
      />
    </div>
  );
};

export default ApplicationProcessing;